<template>
    <section id="resources" class="py-8 md:py-16 bg-neutral-50">
        <div class="px-4 mx-auto max-w-screen-2xl md:px-6 lg:px-8">
            <h2 class="mb-5 text-3xl font-bold text-center md:mb-12 md:text-4xl font-ww-medium" v-html="sectionTitle"></h2>
            <div class="grid grid-cols-1 gap-4 xl:gap-8 sm:grid-cols-2 lg:grid-cols-4">
                <div v-for="(resource, index) in resources" :key="index"
                    class="flex flex-col overflow-hidden transition-all duration-300 -translate-y-2 bg-white shadow-md group rounded-2xl hover:transform hover:shadow-xl">
                    <div class="flex flex-col h-full">
                        <div class="overflow-hidden lg:h-72 group-hover:scale-105 group-hover:duration-300">
                            <nuxt-img :src="resource.image" sizes="xs:263px sm:344px md:651px lg:426px xl:344px" :alt="resource.alt" width="344" height="288"
                                class="object-cover w-full h-full" format="webp" loading="eager" fetchpriority="high" />
                        </div>
                        <div class="flex flex-col justify-between px-6 pt-4">
                         
                                <h3 class="h-full mb-3 text-lg font-bold text-center xl:text-xl font-ww-medium lg:mt-0 sm:mt-3"
                                    v-html="resource.title"></h3>
                                <div class="text-sm text-neutral-700 xl:text-base" v-html="resource.description"></div>
                         

                        </div>
                    </div>

                    <div class="p-4 xl:p-6">
                        <a :href="resource.link"
                            class="flex items-center justify-center w-full gap-2 mx-auto md:max-w-full !bg-lifeboost-orange-300 sm:max-w-md btn-red btn btn-primary font-ww-medium xl:px-7 lg:px-3 xl:text-lg md:text-base xl:py-4 md:py-3"
                            download>
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span v-html="resource.buttonText"></span>
                        </a>
                    </div>

                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    sectionTitle: {
        type: String,
        default: 'Your Brain Health Resources',
    },
    resources: {
        type: Array,
        default: () => [
            {
                title: '30-Day Better Brain Meal Plan',
                description:
                    'A comprehensive nutrition guide to fuel your brain with recipes, shopping lists, and meal schedules.',
                link: 'https://delivery.shopifyapps.com/-/34c92b23d3da5aed/eb29cf9106247c36/download',
                image: '/assets/images/brain-1.webp',
                alt: '30-Day Better Brain Meal Plan',
                buttonText: 'Download PDF',
            },
            {
                title: 'Top-10 Brain Toxins Guide',
                description:
                    'Identify and eliminate harmful substances that may be affecting your cognitive function.',
                link: 'https://delivery.shopifyapps.com/-/042a3158097489b1/653911e59505506f/download',
                image: '/assets/images/brain-2.webp',
                alt: 'Top-10 Brain Toxins Guide',
                buttonText: 'Download PDF',
            },
            {
                title: 'Better Brain Games Workout',
                description:
                    'Mental exercises and puzzles designed to strengthen neural connections and improve memory.',
                link: 'https://delivery.shopifyapps.com/-/d936ae4a1b107d2c/b001c7776e8d785c/download',
                image: '/assets/images/brain-3.webp',
                alt: 'Better Brain Games Workout',
                buttonText: 'Download PDF',
            },
            {
                title: 'Top-20 Brain Health Hacks',
                description:
                    'Quick, actionable techniques to enhance focus, memory, and cognitive performance.',
                link: 'https://delivery.shopifyapps.com/-/224c828345238e38/1b38bd45bb25b39d/download',
                image: '/assets/images/brain-4.webp',
                alt: 'Top-20 Brain Health Hacks',
                buttonText: 'Download PDF',
            },
        ],
    },
});

defineBuilderComponent({
    friendlyName: 'GridItemsButton',
    inputs: [
        {
            name: 'sectionTitle',
            type: 'richText',
            friendlyName: 'Section Title',
            defaultValue: 'Your Brain Health Resources',
        },
        {
            name: 'resources',
            type: 'list',
            friendlyName: 'Resources',
            defaultValue: [
                {
                    title: '30-Day Better Brain Meal Plan',
                    description:
                        'A comprehensive nutrition guide to fuel your brain with recipes, shopping lists, and meal schedules.',
                    link: 'https://delivery.shopifyapps.com/-/34c92b23d3da5aed/eb29cf9106247c36/download',
                    image: '/assets/images/brain-1.webp',
                    alt: '30-Day Better Brain Meal Plan',
                    buttonText: 'Download PDF',
                },
                {
                    title: 'Top-10 Brain Toxins Guide',
                    description:
                        'Identify and eliminate harmful substances that may be affecting your cognitive function.',
                    link: 'https://delivery.shopifyapps.com/-/042a3158097489b1/653911e59505506f/download',
                    image: '/assets/images/brain-2.webp',
                    alt: 'Top-10 Brain Toxins Guide',
                    buttonText: 'Download PDF',
                },
                {
                    title: 'Better Brain Games Workout',
                    description:
                        'Mental exercises and puzzles designed to strengthen neural connections and improve memory.',
                    link: 'https://delivery.shopifyapps.com/-/d936ae4a1b107d2c/b001c7776e8d785c/download',
                    image: '/assets/images/brain-3.webp',
                    alt: 'Better Brain Games Workout',
                    buttonText: 'Download PDF',
                },
                {
                    title: 'Top-20 Brain Health Hacks',
                    description:
                        'Quick, actionable techniques to enhance focus, memory, and cognitive performance.',
                    link: 'https://delivery.shopifyapps.com/-/224c828345238e38/1b38bd45bb25b39d/download',
                    image: '/assets/images/brain-4.webp',
                    alt: 'Top-20 Brain Health Hacks',
                    buttonText: 'Download PDF',
                },
            ],
            subFields: [
                { name: 'title', type: 'text', friendlyName: 'Title' },
                { name: 'description', type: 'text', friendlyName: 'Description' },
                { name: 'link', type: 'text', friendlyName: 'Download Link' },
                {
                    name: 'image',
                    type: 'file',
                    friendlyName: 'Image',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
                },
                { name: 'alt', type: 'text', friendlyName: 'Image Alt Text' },
                { name: 'buttonText', type: 'text', friendlyName: 'Button Text', defaultValue: 'Download PDF' },
            ],
        },
    ],
});
</script>